import React, { Suspense, lazy, useEffect } from 'react';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import MediaDebugPanel from './components/MediaDebugPanel';
import ErrorBoundary from './components/ErrorBoundary';
import { initializeMediaOptimization } from './utils/mediaOptimization';
import { initializePerformanceOptimizations } from './utils/performance';
import './styles/animations.css';

// Lazy load components that are below the fold
const Services = lazy(() => import('./components/Services'));
const About = lazy(() => import('./components/About'));
const Portfolio = lazy(() => import('./components/Portfolio'));
const Contact = lazy(() => import('./components/Contact'));
const Footer = lazy(() => import('./components/Footer'));

// Loading component for suspense fallbacks
const SectionSkeleton = React.memo(() => (
  <div className="py-24 px-4">
    <div className="max-w-7xl mx-auto">
      <div className="animate-pulse">
        <div className="h-16 bg-gray-800 rounded-lg mb-8 max-w-md mx-auto"></div>
        <div className="grid md:grid-cols-3 gap-8">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-64 bg-gray-800 rounded-lg"></div>
          ))}
        </div>
      </div>
    </div>
  </div>
));

// Error fallback for critical app failures
const AppErrorFallback = () => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
    <div className="text-center max-w-md">
      <h1 className="text-4xl font-bold mb-4">
        <span className="text-cyan-400">MEDIA</span>
        <span className="text-white">MOGULZ</span>
      </h1>
      <p className="text-gray-300 mb-6">
        We're experiencing technical difficulties. Please refresh the page or try again later.
      </p>
      <button 
        onClick={() => window.location.reload()}
        className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 px-6 rounded-full transition-colors"
      >
        Refresh Page
      </button>
    </div>
  </div>
);

export default function App() {
  useEffect(() => {
    // Initialize optimizations on app load
    try {
      initializeMediaOptimization();
      initializePerformanceOptimizations();
    } catch (error) {
      console.warn('Failed to initialize optimizations:', error);
    }
  }, []);

  return (
    <ErrorBoundary 
      fallback={<AppErrorFallback />}
      onError={(error, errorInfo) => {
        console.error('Critical app error:', error);
        console.error('App error info:', errorInfo);
      }}
    >
      <div className="min-h-screen bg-black text-white overflow-x-hidden">
        {/* Sticky Navigation */}
        <ErrorBoundary
          fallback={
            <div className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-lg border-b border-gray-800/50 h-16 flex items-center justify-center">
              <span className="text-cyan-400 font-bold">MediaMogulz</span>
            </div>
          }
          onError={(error) => console.warn('Navigation error:', error.message)}
        >
          <Navigation />
        </ErrorBoundary>
        
        {/* Hero loads immediately */}
        <section id="hero">
          <Hero />
        </section>
        
        {/* Other sections load lazily with suspense boundaries */}
        <section id="services">
          <Suspense fallback={<SectionSkeleton />}>
            <Services />
          </Suspense>
        </section>
        
        <section id="about">
          <Suspense fallback={<SectionSkeleton />}>
            <About />
          </Suspense>
        </section>
        
        <section id="portfolio">
          <Suspense fallback={<SectionSkeleton />}>
            <Portfolio />
          </Suspense>
        </section>
        
        <section id="contact">
          <Suspense fallback={<SectionSkeleton />}>
            <Contact />
          </Suspense>
        </section>

        {/* Footer */}
        <ErrorBoundary
          fallback={
            <footer className="bg-black py-8">
              <div className="max-w-7xl mx-auto px-4 text-center">
                <p className="text-gray-400">© 2024 MediaMogulz. All rights reserved.</p>
              </div>
            </footer>
          }
          onError={(error) => console.warn('Footer error:', error.message)}
        >
          <Suspense fallback={
            <footer className="bg-black py-8">
              <div className="max-w-7xl mx-auto px-4 text-center">
                <div className="animate-pulse h-16 bg-gray-800 rounded"></div>
              </div>
            </footer>
          }>
            <Footer />
          </Suspense>
        </ErrorBoundary>

        {/* Debug panel for development */}
        <ErrorBoundary
          fallback={null}
          onError={(error) => console.warn('Debug panel error:', error.message)}
        >
          <MediaDebugPanel />
        </ErrorBoundary>
      </div>
    </ErrorBoundary>
  );
}
